import { globalInitialStateVariable } from './hydration';
import type { Store } from './rtk-types';
import type { ModuleState, ModuleStore } from './types';

/**
 * Маркер объекта серверного снепшота. Свойство не перечисляемое, поэтому не попадает в копии вида `{ ...state }`.
 */
const $SERVER_SNAPSHOT = Symbol('$SERVER_SNAPSHOT');

/** Исходное значение серверного снепшота, которое нельзя пометить собственным свойством (например, `undefined`). */
const $SERVER_SNAPSHOT_VALUE = Symbol('$SERVER_SNAPSHOT_VALUE');

type BoxedServerSnapshot = {
    readonly [$SERVER_SNAPSHOT]: true;
    readonly [$SERVER_SNAPSHOT_VALUE]: unknown;
};

/**
 * Признак того, что сейчас выполняется селектор над серверным снепшотом.
 * В этом режиме привязанные селекторы всех модулей читают серверные снепшоты своих сторов,
 * чтобы кросс-сторный селектор при гидрации целиком считался по серверным данным.
 */
let isServerSnapshotSelect = false;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function hasOwn(record: Record<string, unknown>, key: string) {
    return Object.prototype.hasOwnProperty.call(record, key);
}

function isBoxedServerSnapshot(value: unknown): value is BoxedServerSnapshot {
    return isRecord(value) && $SERVER_SNAPSHOT_VALUE in value;
}

function unboxServerSnapshot<State>(state: State): State {
    if (!isBoxedServerSnapshot(state)) {
        return state;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- коробка хранит исходное значение State
    return state[$SERVER_SNAPSHOT_VALUE] as State;
}

function createBoxedServerSnapshot(value: unknown): BoxedServerSnapshot {
    const snapshot = Object.defineProperties(
        {},
        {
            [$SERVER_SNAPSHOT]: { value: true },
            [$SERVER_SNAPSHOT_VALUE]: { value },
        }
    );

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- свойства заданы через Object.defineProperties
    return Object.freeze(snapshot) as BoxedServerSnapshot;
}

/**
 * Создает функцию, возвращающую серверный снепшот стейта модульного стора.
 *
 * React при гидрации рендерит компоненты по значению `getServerSnapshot` из `useSyncExternalStore`,
 * а после commit сверяет его с `getSnapshot` и при расхождении сам перерисовывает компонент клиентским
 * стейтом. Поэтому серверный снепшот должен совпадать с тем, что видел сервер при рендере, даже если
 * клиентский стейт к моменту гидрации уже изменился (persist, prefetch, реакции).
 *
 * Снепшот собирается по ключам текущего стейта: для слайса, попавшего в `__REDUX_INITIAL_STATE__`,
 * берется серверное значение из дампа, для остальных (unsafe-слайсы, слайсы без серверных данных)
 * текущее клиентское значение. Объект переиспользуется, пока его содержимое не меняется:
 * React требует стабильный результат `getServerSnapshot` в рамках одного рендера.
 *
 * На сервере снепшот равен текущему стейту.
 *
 * @param moduleName – Название модуля.
 * @param store – Redux-стор модуля.
 * @returns Функция, возвращающая серверный снепшот.
 * @internal
 */
export function createServerSnapshotGetter(moduleName: string, store: Store): () => ModuleState {
    if (process.env.BUILD_TARGET !== 'client') {
        return store.getState;
    }

    let cachedSnapshot: Record<string, unknown> | undefined;
    let cachedBoxedSnapshot: BoxedServerSnapshot | undefined;
    let cachedBoxedValue: unknown;

    return function getServerSnapshot() {
        const state: unknown = store.getState();
        if (!isRecord(state)) {
            if (!cachedBoxedSnapshot || !Object.is(cachedBoxedValue, state)) {
                cachedBoxedValue = state;
                cachedBoxedSnapshot = createBoxedServerSnapshot(state);
            }

            return cachedBoxedSnapshot;
        }

        const dump: unknown = global[globalInitialStateVariable]?.[moduleName];
        const dumpRecord = isRecord(dump) ? dump : undefined;

        const keys = Object.keys(state);
        const nextSnapshot: Record<string, unknown> = {};
        const previousSnapshot = cachedSnapshot;
        let isChanged = previousSnapshot === undefined || Object.keys(previousSnapshot).length !== keys.length;

        for (const key of keys) {
            const value = dumpRecord && hasOwn(dumpRecord, key) ? dumpRecord[key] : state[key];
            nextSnapshot[key] = value;

            if (!isChanged && previousSnapshot && (!hasOwn(previousSnapshot, key) || previousSnapshot[key] !== value)) {
                isChanged = true;
            }
        }

        if (!isChanged) {
            return previousSnapshot;
        }

        Object.defineProperty(nextSnapshot, $SERVER_SNAPSHOT, { value: true });
        cachedSnapshot = Object.freeze(nextSnapshot);

        return cachedSnapshot;
    };
}

/**
 * Проверяет, является ли объект серверным снепшотом стора.
 *
 * @param state – Проверяемый стейт.
 * @returns `true`, если стейт получен из `getServerSnapshot`.
 * @internal
 */
export function isServerSnapshot(state: unknown): boolean {
    return isRecord(state) && $SERVER_SNAPSHOT in state;
}

/**
 * Вызывает селектор над переданным снепшотом стейта.
 * Если снепшот серверный, на время вызова включается режим чтения серверных снепшотов
 * во всех привязанных селекторах (см. {@link getStateForSelect}).
 *
 * @param state – Снепшот стейта, переданный `useSyncExternalStore` в селектор.
 * @param select – Селектор.
 * @returns Результат селектора.
 * @internal
 */
export function selectFromSnapshot<State, Result>(state: State, select: (state: State) => Result): Result {
    if (!isServerSnapshot(state)) {
        return select(state);
    }

    const snapshot = unboxServerSnapshot(state);

    if (isServerSnapshotSelect) {
        return select(snapshot);
    }

    isServerSnapshotSelect = true;

    try {
        return select(snapshot);
    } finally {
        isServerSnapshotSelect = false;
    }
}

/**
 * Возвращает стейт стора для вызова привязанного селектора:
 * серверный снепшот внутри вызова над серверным снепшотом, иначе текущий стейт.
 *
 * @param store – Стор модуля.
 * @returns Стейт для селектора.
 * @internal
 */
export function getStateForSelect(store: ModuleStore): ModuleState {
    return isServerSnapshotSelect ? store.getServerSnapshot() : store.getState();
}
